import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showErrorToast,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import Input from '../../../../../../../common/widgets/Input';
import {
  ErrorState,
  ErrorType,
  ValidationError,
} from '../../../../../../benefit/models/Errors';
import { SoCodeTypes } from '../../../../../enums';
import { CodeModel } from '../../../../../models/codes';
import { resources } from '../../../../../resources/resources';
import { codesService } from '../../../../../services/codes';

interface GeneratedCodesDialogProps {
  setGeneratedCodes: (codes: CodeModel[]) => void;
  closeDialog: () => void;
  showPreviewDialog: () => void;
}

export const GeneratedCodesDialog = (props: GeneratedCodesDialogProps) => {
  const { setGeneratedCodes, closeDialog, showPreviewDialog } = props;
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [form, setForm] = useState<{
    codesNumber?: number;
    codeLength?: number;
  }>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const { t: translate } = useTranslation();

  const validateForm = (): boolean => {
    let result = true;
    const newErrors: ErrorState = {};
    if (!form.codesNumber) {
      newErrors.codesNumber = [getRequiredValidationError('codesNumber')];
      result = false;
    }
    if (!form.codeLength) {
      newErrors.codeLength = [getRequiredValidationError('codeLength')];
      result = false;
    }
    setErrors(newErrors);
    return result;
  };

  const onGenerateClick = async () => {
    if (!validateForm()) return;
    await loadDefaultCodes();
  };

  const loadDefaultCodes = async () => {
    setIsButtonLoading(true);
    const resp = await codesService.getGeneratedDefaultCodes(
      form.codesNumber!,
      form.codeLength!,
    );
    setIsButtonLoading(false);
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      setGeneratedCodes(
        resp.data.map(e => ({ type: SoCodeTypes.Default, code: e })),
      );
      showPreviewDialog();
    }
  };

  const clearErrors = (field: string) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.codes.generate.dialog.title)}
        </div>
        <div className="dialog-text centered mb-20">
          {translate(resources.codes.generate.dialog.info)}
        </div>
        <Input
          type="number"
          label={translate(resources.codes.generate.dialog.codesNumber.label)}
          value={form.codesNumber}
          onChange={e =>
            setForm(
              produce(d => {
                d.codesNumber = Number.parseInt(e.value);
              }),
            )
          }
          placeholder={translate(
            resources.codes.generate.dialog.codesNumber.placeholder,
          )}
          mandatory
          errors={errors.codesNumber}
          clearErrors={() => clearErrors('codesNumber')}
          helper={translate(resources.codes.generate.dialog.codesNumber.info)}
        />
        <Input
          type="number"
          label={translate(resources.codes.generate.dialog.codeLength.label)}
          value={form.codeLength}
          onChange={e =>
            setForm(
              produce(d => {
                d.codeLength = Number.parseInt(e.value);
              }),
            )
          }
          placeholder={translate(
            resources.codes.generate.dialog.codeLength.placeholder,
          )}
          mandatory
          errors={errors.codeLength}
          clearErrors={() => clearErrors('codeLength')}
          controlClassName="mb-0"
          helper={translate(resources.codes.generate.dialog.codeLength.info)}
        />
      </div>
      <div className="dialog-actions vertical">
        <Button
          onClick={onGenerateClick}
          loading={isButtonLoading}
          className="btn btn-solid"
        >
          <span className="btn-text">
            {translate(resources.codes.generate.dialog.generateCodes)}
          </span>
        </Button>
      </div>
    </Dialog>
  );
};

const getRequiredValidationError = (target: string): ValidationError => ({
  target,
  code: resources.validation.required,
  severity: 'error',
  placeholders: {},
});
